import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TableSortLabel,
  InputAdornment,
  Select,
  MenuItem as SelectMenuItem,
  Tabs,
  Tab,
  SelectChangeEvent,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { Search, UnfoldMore } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { offerStandaloneService, getStandaloneOfferUrlSegment, type OfferStandalone } from '../../services/offerStandaloneService';
import {
  getEasyEstimateTotalsByStandaloneOffer,
  type EasyEstimateOfferMoneyTotals,
} from '../../services/estimateService';
import userService, { AppUser } from '../../services/userService';
import { useNotification } from '../layouts/Layout';

/** Ten sam zbiór co zakładka „Aktualnie ofertowane” na /offers (OffersStandaloneTab). */
const ACTIVE_OFFERING_STATUSES = new Set<string>([
  'w trakcie',
  'najlepsza oferta',
  'druga oferta',
  'trzecia i dalej oferta',
  'Uzupełnianie dokumentów',
]);

type SortField = 'id' | 'offer_preparer' | 'client_name' | 'estimated_order_value' | 'vat' | 'brutto';

export type EasyEstimateHomeProps = {
  /** Inkrementacja po dodaniu oferty — ponowne pobranie listy (bez podwójnego pierwszego ładowania). */
  listRefreshKey?: number;
};

const EasyEstimateHome: React.FC<EasyEstimateHomeProps> = ({ listRefreshKey = 0 }) => {
  const [offers, setOffers] = React.useState<OfferStandalone[]>([]);
  const [totalsByOfferId, setTotalsByOfferId] = React.useState<Record<string, EasyEstimateOfferMoneyTotals>>({});
  const [loading, setLoading] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const { showNotification } = useNotification();

  const [searchText, setSearchText] = React.useState('');
  const [preparer, setPreparer] = React.useState('');
  const [users, setUsers] = React.useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);

  const [sortField, setSortField] = React.useState<SortField>('id');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

  const sortLabelBaseSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px',
    },
  } as const;
  const StrongSortIcon = () => <UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />;
  const MutedSortIcon = () => <UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />;
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as React.ComponentType<{ className: string }>,
    sx: {
      ...sortLabelBaseSx,
      '& .MuiTableSortLabel-icon': {
        opacity: 1,
        width: 12,
        height: 12,
        ml: '4px',
        color: isActive ? '#2D2D32' : '#d4d4d9',
      },
    },
  });

  React.useEffect(() => {
    userService.listUsersPublicForBusinessForms().then(setUsers).catch(() => {});
  }, []);

  const fetchOffers = React.useCallback(async () => {
    try {
      setLoading(true);
      const [data, totalsRes] = await Promise.all([
        offerStandaloneService.getAllOffers(),
        getEasyEstimateTotalsByStandaloneOffer().catch(() => {
          showNotification('Nie udało się pobrać sum z kosztorysów (Netto/VAT/Brutto)', 'error');
          return { totals: {} as Record<string, EasyEstimateOfferMoneyTotals> };
        }),
      ]);
      setOffers(data);
      setTotalsByOfferId(totalsRes.totals || {});
    } catch {
      showNotification('Nie udało się pobrać ofert', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  React.useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  React.useEffect(() => {
    if (listRefreshKey < 1) return;
    fetchOffers();
  }, [listRefreshKey, fetchOffers]);

  const activeOffers = React.useMemo(
    () => offers.filter((o) => ACTIVE_OFFERING_STATUSES.has(o.status)),
    [offers]
  );
  /** Archiwum: oferty, których status nie jest „w trakcie” (jak w module Oferty — ten sam rekord może być widoczny w obu zakładkach). */
  const archiveOffers = React.useMemo(() => offers.filter((o) => o.status !== 'w trakcie'), [offers]);

  const matchesOfferFilters = React.useCallback(
    (offer: OfferStandalone) => {
      const matchesSearch =
        searchText === '' ||
        offer.id.toLowerCase().includes(searchText.toLowerCase()) ||
        (offer.client_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (offer.order_name || '').toLowerCase().includes(searchText.toLowerCase());
      const matchesPreparer = preparer === '' || offer.offer_preparer === preparer;
      return matchesSearch && matchesPreparer;
    },
    [searchText, preparer]
  );

  const tabCounts = React.useMemo(
    () => ({
      active: activeOffers.filter(matchesOfferFilters).length,
      archive: archiveOffers.filter(matchesOfferFilters).length,
    }),
    [activeOffers, archiveOffers, matchesOfferFilters]
  );

  const baseList = tabValue === 0 ? activeOffers : archiveOffers;
  const filteredOffers = baseList.filter(matchesOfferFilters);

  const moneyTotalsForOffer = React.useCallback(
    (offer: OfferStandalone): EasyEstimateOfferMoneyTotals | null => totalsByOfferId[offer.id] ?? null,
    [totalsByOfferId]
  );

  const getComparable = React.useCallback(
    (offer: OfferStandalone, field: SortField): string | number => {
      const t = moneyTotalsForOffer(offer);
      switch (field) {
        case 'id': {
          if (typeof offer.offer_sequence_number === 'number') return offer.offer_sequence_number;
          if (offer.offer_id) return offer.offer_id;
          return offer.id;
        }
        case 'offer_preparer':
          return offer.offer_preparer || '';
        case 'client_name':
          return offer.client_name || '';
        case 'estimated_order_value':
          return t === null ? -1 : t.net;
        case 'vat':
          return t === null ? -1 : t.vat;
        case 'brutto':
          return t === null ? -1 : t.brutto;
        default:
          return '';
      }
    },
    [moneyTotalsForOffer]
  );

  const handleRequestSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const visibleOffers = React.useMemo(() => {
    return [...filteredOffers].sort((a, b) => {
      const av = getComparable(a, sortField);
      const bv = getComparable(b, sortField);
      let res = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        res = av - bv;
      } else {
        res = String(av).localeCompare(String(bv), 'pl', { sensitivity: 'base', numeric: true });
      }
      return sortOrder === 'asc' ? res : -res;
    });
  }, [filteredOffers, sortField, sortOrder, getComparable]);

  const formatMoney = (n: number | null) => {
    if (n === null) return '—';
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const handlePreparerChange = (event: SelectChangeEvent) => {
    setPreparer(event.target.value as string);
  };

  const cellStyle = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '140px',
    padding: '2px 6px',
    fontSize: '0.72rem',
  };
  const clientCellStyle = { ...cellStyle, maxWidth: '260px' };
  const ofertaCellStyle = { ...cellStyle, maxWidth: '520px', paddingLeft: '4px' };

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: '300px 300px' },
          columnGap: '24px',
          rowGap: 2,
          alignItems: 'start',
          mb: 2,
          bgcolor: '#FAFAFA',
        }}
      >
        <TextField
          size="small"
          placeholder="Szukaj w ofertach"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try {
                  (e.currentTarget as HTMLInputElement).blur();
                } catch {}
              }
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', md: 300 },
            mt: { xs: 0, md: '20px' },
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px',
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 },
          }}
        />

        <Box sx={{ width: { xs: '100%', md: 300 }, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>
            Przygotowuje ofertę
          </Typography>
          <Select
            size="small"
            fullWidth
            value={preparer}
            onChange={handlePreparerChange}
            displayEmpty
            renderValue={preparer !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps: Record<string, unknown>) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                height: 36,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: preparer ? '#2D2D32' : '#B9B9B9',
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966 !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            }}
          >
            <SelectMenuItem value="">Wszyscy</SelectMenuItem>
            {users.map((u) => (
              <SelectMenuItem key={u.user_id} value={u.user_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                  <Box component="span">{u.display_name || u.email}</Box>
                </Box>
              </SelectMenuItem>
            ))}
            <SelectMenuItem value="AI">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src="/assets/ai_badge_unitresco.svg" sx={{ width: 22, height: 22 }} />
                <Box component="span">AI</Box>
              </Box>
            </SelectMenuItem>
          </Select>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: 0, mb: 0 }}>
        <Tabs
          value={tabValue}
          onChange={(_, next) => setTabValue(next)}
          aria-label="EasyEstimate — zakładki ofert"
          sx={{
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' },
          }}
        >
          <Tab
            id="easy-estimate-tab-0"
            disableRipple
            label={`Aktualnie ofertowane (${tabCounts.active})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' },
            }}
          />
          <Tab
            id="easy-estimate-tab-1"
            disableRipple
            label={`Archiwum (${tabCounts.archive})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': {
                mt: 0,
                height: 40,
                bgcolor: '#E6E6EC',
                color: '#020242',
                fontWeight: 500,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' },
            }}
          />
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        id={`easy-estimate-tabpanel-${tabValue}`}
        aria-labelledby={`easy-estimate-tab-${tabValue}`}
      >
        <OffersSubTable
          visibleOffers={visibleOffers}
          loading={loading}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleRequestSort}
          getSortLabelProps={getSortLabelProps}
          cellStyle={cellStyle}
          clientCellStyle={clientCellStyle}
          ofertaCellStyle={ofertaCellStyle}
          formatMoney={formatMoney}
          moneyTotalsForOffer={moneyTotalsForOffer}
        />
      </Box>
    </Box>
  );
};

type SubProps = {
  visibleOffers: OfferStandalone[];
  loading: boolean;
  sortField: SortField;
  sortOrder: 'asc' | 'desc';
  onSort: (f: SortField) => void;
  getSortLabelProps: (active: boolean) => Record<string, unknown>;
  cellStyle: Record<string, unknown>;
  clientCellStyle: Record<string, unknown>;
  ofertaCellStyle: Record<string, unknown>;
  formatMoney: (n: number | null) => string;
  moneyTotalsForOffer: (o: OfferStandalone) => EasyEstimateOfferMoneyTotals | null;
};

function OffersSubTable({
  visibleOffers,
  loading,
  sortField,
  sortOrder,
  onSort,
  getSortLabelProps,
  cellStyle,
  clientCellStyle,
  ofertaCellStyle,
  formatMoney,
  moneyTotalsForOffer,
}: SubProps) {
  const footerSums = React.useMemo(() => {
    let net = 0;
    let vat = 0;
    let brutto = 0;
    for (const offer of visibleOffers) {
      const t = moneyTotalsForOffer(offer);
      if (!t) continue;
      net += t.net;
      vat += t.vat;
      brutto += t.brutto;
    }
    return {
      net: Math.round(net * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      brutto: Math.round(brutto * 100) / 100,
    };
  }, [visibleOffers, moneyTotalsForOffer]);

  const showFooter =
    !loading &&
    visibleOffers.length > 0 &&
    visibleOffers.some((o) => moneyTotalsForOffer(o) !== null);

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, bgcolor: 'rgba(255,255,255,0.5)' }}>
          <CircularProgress size={36} />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 'min(70vh, 720px)',
          overflow: 'auto',
          border: '1px solid #d0d0d6',
          borderRadius: 1,
          boxShadow: 'none',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: 320, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel
                  {...getSortLabelProps(sortField === 'id')}
                  active={sortField === 'id'}
                  direction={sortOrder}
                  onClick={() => onSort('id')}
                >
                  Oferta
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: 56, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel
                  {...getSortLabelProps(sortField === 'offer_preparer')}
                  active={sortField === 'offer_preparer'}
                  direction={sortOrder}
                  onClick={() => onSort('offer_preparer')}
                >
                  KP
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...clientCellStyle, maxWidth: 'none', width: 220, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel
                  {...getSortLabelProps(sortField === 'client_name')}
                  active={sortField === 'client_name'}
                  direction={sortOrder}
                  onClick={() => onSort('client_name')}
                >
                  Zamawiający
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', width: 110, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel
                  {...getSortLabelProps(sortField === 'estimated_order_value')}
                  active={sortField === 'estimated_order_value'}
                  direction={sortOrder}
                  onClick={() => onSort('estimated_order_value')}
                >
                  Netto
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', width: 100, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel {...getSortLabelProps(sortField === 'vat')} active={sortField === 'vat'} direction={sortOrder} onClick={() => onSort('vat')}>
                  VAT
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', width: 110, backgroundColor: '#FAFAFA' }}>
                <TableSortLabel
                  {...getSortLabelProps(sortField === 'brutto')}
                  active={sortField === 'brutto'}
                  direction={sortOrder}
                  onClick={() => onSort('brutto')}
                >
                  Brutto
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOffers.map((offer, index) => {
              const displayId =
                (offer as { offer_id?: string }).offer_id ||
                ((offer as { offer_sequence_number?: number }).offer_sequence_number &&
                (offer as { created_at?: string }).created_at
                  ? `OF.${(offer as { offer_sequence_number?: number }).offer_sequence_number}.${new Date((offer as { created_at?: string }).created_at as string).getFullYear()}`
                  : offer.id.substring(0, 8));
              const t = moneyTotalsForOffer(offer);
              const net = t?.net ?? null;
              const vat = t?.vat ?? null;
              const brutto = t?.brutto ?? null;
              return (
                <TableRow hover key={offer.id + index}>
                  <TableCell sx={{ ...ofertaCellStyle, maxWidth: 'none' }}>
                    <span
                      style={{
                        fontSize: '0.86rem',
                        color: '#2D2D32',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.25,
                        whiteSpace: 'normal',
                      }}
                    >
                      <RouterLink
                        to={`/app/easy-estimate/offer/${encodeURIComponent(getStandaloneOfferUrlSegment(offer))}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          textDecoration: 'underline',
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px',
                          color: '#3F3FDC',
                        }}
                      >
                        {displayId}
                      </RouterLink>
                      <span>{` — ${offer.order_name || ''}`}</span>
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, maxWidth: 'none', px: '2px' }}>
                    {offer.offer_preparer === '__AI__' ? (
                      <Avatar sx={{ width: 22, height: 22, bgcolor: 'transparent', p: 0 }} title="Wprowadzone przez AI">
                        <img src="/assets/ai_badge_unitresco.svg" alt="AI" width={22} height={22} style={{ display: 'block' }} />
                      </Avatar>
                    ) : offer.offer_preparer ? (
                      <Avatar src={offer.offer_preparer_avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: '0.75rem' }}>
                        {(offer.offer_preparer_name || '?').slice(0, 1)}
                      </Avatar>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell sx={{ ...clientCellStyle, maxWidth: 'none' }}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.25,
                        whiteSpace: 'normal',
                      }}
                    >
                      {offer.client_name}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(net)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(vat)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(brutto)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          {showFooter && (
            <TableFooter>
              <TableRow
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  backgroundColor: '#ececf2',
                  '& .MuiTableCell-root': {
                    borderTop: '2px solid #d0d0d6',
                    fontWeight: 600,
                    fontSize: '0.86rem',
                    color: '#202124',
                    fontVariantNumeric: 'tabular-nums',
                  },
                }}
              >
                <TableCell sx={{ ...ofertaCellStyle, maxWidth: 'none' }}>Razem</TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', px: '2px' }} />
                <TableCell sx={{ ...clientCellStyle, maxWidth: 'none' }} />
                <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(footerSums.net)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(footerSums.vat)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellStyle, maxWidth: 'none', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(footerSums.brutto)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EasyEstimateHome;
